import { setErrorModal } from '@app/slices/modals';
import {
  GROUPING_CENTER,
  GROUPING_CLASS,
  GROUPING_NONE,
} from '@dashboard/constants/identification';
import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  CHECKBOX_STATES,
  CheckboxContainer,
  Col,
  Grid,
  Input,
  InputContainer,
  RSelect,
  Title,
} from '@common/components';
import ClassFilters from '@dashboard/components/ClassFilters/ClassFilters';
import DateFilters from '@dashboard/components/DateFilters/DateFilters';
import RadioContainer, { RadioOption } from '@common/components/Radio/Radio';
import {
  selectData,
  setIsLevelOfImprovementObtainedReportSelected,
  setIsStudentIdentificationReportDetailed,
  setIsStudentIdentificationReportSelected,
  setMinimumWorkedTimeByWorkingGroup,
  setSelectedCenterClassesMap,
  setSelectedGender,
  setSelectedGrouping,
  setSelectedPublicationsUsedByWorkingGroup,
  setSelectedUnits,
  setShowReport,
  setStudentIdentificationStats,
  setLevelOfImprovementObtainedStats,
  setSelectedCenterToShow,
  setSelectedClassToShow,
} from '@dashboard/slices/identificationStats';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import {
  isDateFilterApply,
  selectSelectedCenters,
} from '@dashboard/slices/filters';

import viewClasses from './IdentificationStatsView.module.scss';

function IdentificationStatsFilters({
  loading,
  publicationOptions,
  unitOptions,
}) {
  const dispatch = useAppDispatch();
  const currentStats = useAppSelector(selectData);
  const currentFilteredCenters = useAppSelector(selectSelectedCenters);
  const isDateApply = useAppSelector(isDateFilterApply);
  const { t } = useTranslation('dashboard\\IdentificationStatsView');
  const gendersT = useTranslation('dashboard\\Genders');
  const publicationsT = useTranslation('dashboard\\Publications');

  const selectedCenterClassesMap = currentStats.selectedCenterClassesMap || {};

  const handleClickGenerateReport = () => {
    if (
      !currentStats.isLevelOfImprovementObtainedReportSelected &&
      !currentStats.isStudentIdentificationReportSelected
    ) {
      dispatch(
        setErrorModal({
          data: { message: t('selectAtLeastOneReportTypeError') },
        }),
      );
    }

    if (
      !currentStats.selectedClassIds.length ||
      !currentStats.selectedUnits.length ||
      !currentStats.selectedPublicationsUsedByWorkingGroup.length
    ) {
      dispatch(setErrorModal({ data: { message: t('emptyFiltersError') } }));
      return;
    }

    dispatch(setStudentIdentificationStats(null));
    dispatch(setLevelOfImprovementObtainedStats(null));
    dispatch(setSelectedCenterToShow(null));
    dispatch(setSelectedClassToShow(null));
    dispatch(setShowReport(true));
  };

  return (
    <>
      <DateFilters isLoading={loading} />
      <ClassFilters
        centerClassesMap={currentStats.centerClassesMap}
        centers={currentFilteredCenters}
        selectedCenterClassesMap={selectedCenterClassesMap}
        onChangeSelectedClasses={(value) =>
          dispatch(setSelectedCenterClassesMap(value))
        }
      />
      <Grid justify="start" align="middle">
        <Col xss={12} xs={12} sm={3}>
          <RSelect
            data={unitOptions}
            onChange={(options) => dispatch(setSelectedUnits(options))}
            val={currentStats.selectedUnits}
            placeholder={t('selectEvaluationsLabel')}
            isMultiple
          />
        </Col>
        <Col xss={12} xs={12} sm={3}>
          <RSelect
            data={[
              { label: gendersT.t('all'), value: 'all' },
              { label: gendersT.t('male'), value: 'M' },
              { label: gendersT.t('female'), value: 'F' },
            ]}
            val={
              currentStats.selectedGender === null
                ? { label: gendersT.t('all'), value: 'all' }
                : currentStats.selectedGender
            }
            onChange={(option) => {
              dispatch(setSelectedGender(option));
            }}
            placeholder={gendersT.t('label')}
          />
        </Col>
      </Grid>

      <Title type="h3" color="grey-400">
        {t('workingGroupFilterSection')}
      </Title>
      <Grid justify="start" align="middle">
        <Col xss={12} xs={12} sm={3}>
          <RSelect
            data={publicationOptions}
            onChange={(options) =>
              dispatch(setSelectedPublicationsUsedByWorkingGroup(options))
            }
            val={currentStats.selectedPublicationsUsedByWorkingGroup}
            placeholder={publicationsT.t('selectFormatUsedByWorkingGroupLabel')}
            isMultiple
          />
        </Col>

        <Col xss={12} xs={12} sm={3}>
          <InputContainer
            placeholder={t('minimumWorkedTimeLabel')}
            name="minimumWorkedTimeByWorkingGroup"
          >
            <Input
              type="duration"
              placeholder={t('minimumWorkedTimeLabel')}
              name="minimumWorkedTimeByWorkingGroup"
              fullWidth
              onChange={(value) => {
                dispatch(setMinimumWorkedTimeByWorkingGroup(value));
              }}
              value={currentStats.minimumWorkedTimeByWorkingGroup}
            />
          </InputContainer>
        </Col>
      </Grid>

      <Title type="h3" color="grey-400">
        {t('reportFilterSection')}
      </Title>
      <Grid justify="start" align="middle">
        <Col lg={2} md={12}>
          <span>{t('reportFilterType')}</span>
        </Col>
        <Col lg={10} md={12} flex justify="start">
          <CheckboxContainer
            className={viewClasses.rowCheckbox}
            label={t('levelOfImprovementObtainedAndEffectSize')}
            onChange={(e) => {
              dispatch(
                setIsLevelOfImprovementObtainedReportSelected(e.target.checked),
              );
            }}
            value="levelOfImprovementObtainedAndEffectSize"
            id="levelOfImprovementObtainedAndEffectSize"
            checked={
              currentStats.isLevelOfImprovementObtainedReportSelected
                ? CHECKBOX_STATES.Checked
                : CHECKBOX_STATES.Empty
            }
          />
          <CheckboxContainer
            className={viewClasses.rowCheckbox}
            label={t('studentIdentification')}
            onChange={(e) => {
              dispatch(
                setIsStudentIdentificationReportSelected(e.target.checked),
              );
            }}
            value="studentIdentification"
            id="studentIdentification"
            checked={
              currentStats.isStudentIdentificationReportSelected
                ? CHECKBOX_STATES.Checked
                : CHECKBOX_STATES.Empty
            }
          />
          <RadioContainer
            name="studentIdentificationReportDetail"
            options={[
              <RadioOption value="global" label={t('globalReport')} />,
              <RadioOption value="detailed" label={t('detailedReport')} />,
            ]}
            value={
              currentStats.isStudentIdentificationReportDetailed
                ? 'detailed'
                : 'global'
            }
            onChange={(value) => {
              dispatch(
                setIsStudentIdentificationReportDetailed(value === 'detailed'),
              );
            }}
            customStyle={{
              display: currentStats.isStudentIdentificationReportSelected
                ? 'inherit'
                : 'none',
              marginLeft: '20px',
              width: 'auto',
            }}
          />
        </Col>
      </Grid>
      <Grid justify="start" align="middle">
        <Col lg={2} md={12}>
          <span>{t('reportFilterGroupingLevel')}</span>
        </Col>
        <Col lg={10} md={12}>
          <RadioContainer
            name="groupingLevel"
            options={[
              <RadioOption
                value={GROUPING_NONE}
                label={t('reportFilterGroupingNone')}
              />,
              <RadioOption
                value={GROUPING_CENTER}
                label={t('reportFilterGroupingByCenter')}
              />,
              <RadioOption
                value={GROUPING_CLASS}
                label={t('reportFilterGroupingByClass')}
              />,
            ]}
            value={currentStats.selectedGrouping || GROUPING_NONE}
            onChange={(value) => {
              dispatch(
                setSelectedGrouping(value === GROUPING_NONE ? null : value),
              );
            }}
          />
        </Col>
      </Grid>
      <Grid justify="start" align="middle">
        <Col lg={3} md={12}>
          <Button
            color="accent"
            variant="flat"
            onClick={handleClickGenerateReport}
            pointer
            fullWidth
            disabled={!isDateApply}
          >
            {t('generateReport')}
          </Button>
        </Col>
      </Grid>
    </>
  );
}

IdentificationStatsFilters.propTypes = {
  publicationOptions: PropTypes.array.isRequired,
  unitOptions: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default IdentificationStatsFilters;
